//Game logic

import { GAME_CONFIG } from '../utils/gameConfig';
import { TileType, GameTile, Tree, GameAction, User, Forest } from '../types';

export interface Position {
  x: number;
  y: number;
}

export interface MovementResult {
  newPosition: Position;
  canMove: boolean;
  reason?: string;
}

//Check if a position is within the game grid bounds
export const isPositionInBounds = (x: number, y: number): boolean => {
  return x >= 0 && x < GAME_CONFIG.GRID_WIDTH && 
         y >= 0 && y < GAME_CONFIG.GRID_HEIGHT;
};

//Get tile type at specific position
export const getTileType = (x: number, y: number): TileType => {

  //Border tiles
  if (x === 0 || x === GAME_CONFIG.GRID_WIDTH - 1 || 
      y === 0 || y === GAME_CONFIG.GRID_HEIGHT - 1) {
    return 'border';
  }
  
  //Walkable border
  if (x === 1 || x === GAME_CONFIG.GRID_WIDTH - 2 || 
      y === 1 || y === GAME_CONFIG.GRID_HEIGHT - 2) {
    return 'walkable';
  }
  
  //Inner area
  const adjustedX = x - 2; //To account for border
  const adjustedY = y - 2; //account for border
  

  const clusterX = adjustedX % 4;
  const clusterY = adjustedY % 4;
  
  const isPlantableX = clusterX >= 1 && clusterX <= 3; //Positions 1, 2, 3 are plantable
  const isPlantableY = clusterY >= 1 && clusterY <= 3; //Positions 1, 2, 3 are plantable
  
  if (isPlantableX && isPlantableY) {
    return 'plantable';
  } else {
    return 'walkable';
  }
};

//Check if navigable
//ToDo: Replacing this with mapping, may need to delete this
export const isTileWalkable = (x: number, y: number, trees?: Map<string, boolean>): boolean => {
  //Check bounds
  if (!isPositionInBounds(x, y)) {
    return false;
  }
  
  const tileType = getTileType(x, y);
  
  //Non-navigable tiles
  if (tileType === 'border') {
    return false;
  }
  
  //Walkable
  if (tileType === 'walkable') {
    return true;
  }
  
  //Walkable if no tree
  if (tileType === 'plantable') {
    const treeKey = `${x},${y}`;
    return !trees?.has(treeKey);
  }
  
  return false;
};

//Calculate new position based on direction
export const calculateMovement = (
  currentX: number, 
  currentY: number, 
  direction: 'up' | 'down' | 'left' | 'right',
  trees?: Map<string, boolean>
): MovementResult => {
  let newX = currentX;
  let newY = currentY;
  
  //Calculate new position
  switch (direction) {
    case 'up':
      newY = currentY - 1;
      break;
    case 'down':
      newY = currentY + 1;
      break;
    case 'left':
      newX = currentX - 1;
      break;
    case 'right':
      newX = currentX + 1;
      break;
  }
  
  const newPosition = { x: newX, y: newY };
  
  //Movement validity
  if (!isPositionInBounds(newX, newY)) {
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason: 'Out of bounds'
    };
  }
  
  if (!isTileWalkable(newX, newY, trees)) {
    const tileType = getTileType(newX, newY);
    const reason = tileType === 'border' ? 'Cannot walk through border' : 
                  tileType === 'plantable' ? 'Tree is planted here' : 
                  'Tile not walkable';
    
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason
    };
  }
  
  return {
    newPosition,
    canMove: true
  };
};

//Calculate direction from movement
export const getDirectionFromMovement = (
  fromX: number, 
  fromY: number, 
  toX: number, 
  toY: number
): 'up' | 'down' | 'left' | 'right' | null => {
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;
  
  if (deltaY < 0) return 'up';
  if (deltaY > 0) return 'down';
  if (deltaX < 0) return 'left';
  if (deltaX > 0) return 'right';
  
  return null; //No movement
};

//Update viewport
export const updateViewport = (
  playerX: number,
  playerY: number,
  viewportWidth: number,
  viewportHeight: number
): Position => {
  //Try to center player in viewport
  let newViewportX = playerX - Math.floor(viewportWidth / 2);
  let newViewportY = playerY - Math.floor(viewportHeight / 2);
  
  //Clamp viewport to grid boundaries
  newViewportX = Math.max(0, Math.min(GAME_CONFIG.GRID_WIDTH - viewportWidth, newViewportX));
  newViewportY = Math.max(0, Math.min(GAME_CONFIG.GRID_HEIGHT - viewportHeight, newViewportY));
  
  return { x: newViewportX, y: newViewportY };
};


//New Approach 9/11/25 - ToDo: Test and see if this is actually better
//Convert Tree array to Map for efficient collision detection
export const createTreeMap = (trees: Tree[]): Map<string, Tree> => {
  const treeMap = new Map<string, Tree>();
  trees.forEach(tree => {
    const key = `${tree.position.x},${tree.position.y}`;
    treeMap.set(key, tree);
  });
  return treeMap;
};

//Does position have a tree?
export const hasTreeAt = (x: number, y: number, trees: Tree[]): boolean => {
  return trees.some(tree => tree.position.x === x && tree.position.y === y);
};

//Get tree
export const getTreeAt = (x: number, y: number, trees: Tree[]): Tree | null => {
  return trees.find(tree => tree.position.x === x && tree.position.y === y) || null;
};

//Get adjacent trees
export const getAdjacentTrees = (x: number, y: number, trees: Tree[]): Tree[] => {
  const adjacent: Tree[] = [];
  
  //Check all avail adjacent positions
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue; //except current position
      
      const checkX = x + dx;
      const checkY = y + dy;
      
      const tree = getTreeAt(checkX, checkY, trees);
      if (tree) {
        adjacent.push(tree);
      }
    }
  }
  
  return adjacent;
};

//Plantable && Empty?
export const isPlantableAndEmpty = (x: number, y: number, trees: Tree[]): boolean => {
  const tileType = getTileType(x, y);
  
  console.log('isPlantableAndEmpty debug:', {
    position: { x, y },
    tileType,
    treesCount: trees.length
  });
  
  if (tileType !== 'plantable') {
    console.log('Not plantable tile type:', tileType);
    return false;
  }
  
  const hasTree = hasTreeAt(x, y, trees);
  console.log('Has tree at position:', hasTree);
  
  return !hasTree;
};


//Check specific direction
export const getTargetInDirection = (
  x: number, 
  y: number, 
  direction: 'up' | 'down' | 'left' | 'right'
): { x: number; y: number } => {
  switch (direction) {
    case 'up': return { x, y: y - 1 };
    case 'down': return { x, y: y + 1 };
    case 'left': return { x: x - 1, y };
    case 'right': return { x: x + 1, y };
  }
};


//Get available actions at position
export const getAvailableActions = (
  x: number, 
  y: number, 
  direction: 'up' | 'down' | 'left' | 'right',
  user: User, 
  forest: Forest | null,
  trees: Tree[] = []
): GameAction[] => {
  const actions: GameAction[] = [];
  
  console.log('getAvailableActions debug:', {
    position: { x, y },
    direction,
    userSeeds: user.seeds,
    userWater: user.water,
    forestExists: !!forest,
    forestName: forest?.name || 'No forest',
    treesCount: trees.length
  });
  
  const tileType = getTileType(x, y);
  const hasTree = hasTreeAt(x, y, trees);
  const isPlantable = isPlantableAndEmpty(x, y, trees);
  const hasSeeds = user.seeds > 0;
  
  console.log('Plant seed check:', {
    tileType,
    hasTree,
    isPlantable,
    hasSeeds,
    canPlant: isPlantable && hasSeeds
  });
  
  if (isPlantable && hasSeeds) {
    console.log('Adding plant seed action');
    actions.push({ type: 'plant_seed', position: { x, y } });
  }
  
  if (user.water > 0) {
    const target = getTargetInDirection(x, y, direction);
    const targetTree = getTreeAt(target.x, target.y, trees);
    
    console.log('Water tree check:', {
      target,
      targetTree: !!targetTree,
      targetTreeOwner: targetTree?.planterName || 'None',
      hasWater: user.water > 0
    });
    
    if (targetTree) {
      console.log('Adding water tree action for:', targetTree.planterName);
      actions.push({ type: 'water_tree', treeId: targetTree.id });
    }
  }
  
  //Exit option
  console.log('Adding exit action');
  actions.push({ type: 'exit_menu' });
  
  console.log('Final actions:', actions);
  return actions;
};

//ToDo: This replaces isTileWalkable
export const isTileWalkableWithTrees = (x: number, y: number, trees: Tree[] = []): boolean => {
  //Check bounds
  if (!isPositionInBounds(x, y)) {
    return false;
  }
  
  const tileType = getTileType(x, y);
  
  //Non-navigable tiles
  if (tileType === 'border') {
    return false;
  }
  
  //Walkable
  if (tileType === 'walkable') {
    return true;
  }
  
  //Plantable tiles are walkable if no tree
  if (tileType === 'plantable') {
    return !hasTreeAt(x, y, trees);
  }
  
  return false;
};


//ToDo: This replace calculateMovement
export const calculateMovementWithTrees = (
  currentX: number, 
  currentY: number, 
  direction: 'up' | 'down' | 'left' | 'right',
  trees: Tree[] = []
): MovementResult => {
  let newX = currentX;
  let newY = currentY;
  
  //Calculate new position
  switch (direction) {
    case 'up': newY = currentY - 1; break;
    case 'down': newY = currentY + 1; break;
    case 'left': newX = currentX - 1; break;
    case 'right': newX = currentX + 1; break;
  }
  
  const newPosition = { x: newX, y: newY };
  
  //Check bounds
  if (!isPositionInBounds(newX, newY)) {
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason: 'Out of bounds'
    };
  }


  const tileType = getTileType(newX, newY);
  
  if (tileType === 'border') {
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason: 'Cannot walk through border'
    };
  }
  
  if (tileType === 'plantable' && hasTreeAt(newX, newY, trees)) {
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason: 'Tree is planted here'
    };
  }
  
  //Check walkability
  if (!isTileWalkableWithTrees(newX, newY, trees)) {
    const tileType = getTileType(newX, newY);
    const hasTree = hasTreeAt(newX, newY, trees);
    
    const reason = tileType === 'border' ? 'Cannot walk through border' : 
                  hasTree ? 'Tree is planted here' : 
                  'Tile not walkable';
    
    return {
      newPosition: { x: currentX, y: currentY },
      canMove: false,
      reason
    };
  }
  
  return {
    newPosition,
    canMove: true
  };
};
